// Punto único de creación del cliente de Supabase.
// Ninguna app debe instanciar `createClient` directamente: todas importan
// desde aquí, para que la configuración (URL, anon key, opciones de auth)
// viva en un solo lugar y sea auditable.
//
// Fase 3 (Autenticación) — ver docs/adr y supabase/migrations para el Auth
// Hook `custom_access_token_hook`, que agrega `empresa_id` y `rol` como
// custom claims en la raíz del JWT (documento de base de datos, sección
// 3.3). `getClaims()` de este archivo es el lado cliente de ese mecanismo.

import { createClient } from '@supabase/supabase-js';

import { createCookieStorage } from './storage.js';

/**
 * @typedef {object} CookieOptions
 * @property {string} [domain]
 * @property {number} [maxAge]
 * @property {string} [path]
 * @property {boolean} [secure]
 * @property {'lax' | 'strict' | 'none'} [sameSite]
 */

/** @type {CookieOptions} */
const COOKIE_OPTIONS = {
  domain: '.vimosdigital.com',
  maxAge: 60 * 60 * 24 * 365,
  path: '/',
  sameSite: 'lax',
  secure: true,
};

/**
 * Crea un cliente de Supabase.
 *
 * Pensado para uso en el navegador (las 3 apps son SPAs servidas por Vite):
 * lee `VITE_SUPABASE_URL`/`VITE_SUPABASE_ANON_KEY` de `import.meta.env` —
 * Vite solo expone al bundle las variables con ese prefijo, por eso no se
 * usa `SUPABASE_URL`/`SUPABASE_ANON_KEY` a secas aquí (esas son para
 * scripts que corren en Node, fuera de este paquete).
 *
 * No se necesita llamar a esta función salvo que se requiera una
 * configuración distinta a la del singleton `supabase` exportado abajo
 * (por ejemplo, en un test).
 *
 * @param {SupabaseClientOverrides} [overrides]
 */
export function createSupabaseClient(overrides = {}) {
  const url = overrides.url ?? import.meta.env?.VITE_SUPABASE_URL;
  const anonKey = overrides.anonKey ?? import.meta.env?.VITE_SUPABASE_ANON_KEY;

  if (!url || !anonKey) {
    throw new Error(
      '[supabase-client] Faltan VITE_SUPABASE_URL y/o VITE_SUPABASE_ANON_KEY. ' +
        'Completa tu .env a partir de .env.example (sección Supabase).',
    );
  }

  return createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      // Storage basado en cookies con domain=.vimosdigital.com para compartir
      // sesión entre subdominios. Sin esto, Supabase JS usa localStorage por
      // defecto, que es por-origen: la sesión creada en app NO sería visible
      // en panel → ciclo app→panel→app.
      storage: createCookieStorage(COOKIE_OPTIONS),
      cookieOptions: COOKIE_OPTIONS,
    },
  });
}

/**
 * Cliente singleton — usar esta instancia desde las apps salvo necesidad
 * explícita de otra configuración. Se crea de forma perezosa (no al
 * importar el módulo) para que un entorno sin las variables de entorno
 * configuradas (por ejemplo, ejecutando lint o tests que no tocan
 * Supabase) no falle solo por importar este archivo.
 *
 * @type {import('@supabase/supabase-js').SupabaseClient | null}
 */
let _cliente = null;

/**
 * @returns {import('@supabase/supabase-js').SupabaseClient}
 */
export function supabaseClient() {
  if (!_cliente) {
    _cliente = createSupabaseClient();
  }
  return _cliente;
}

/**
 * @typedef {object} ClaimsPlataforma
 * @property {string | null} empresa_id - `null` para super_admin, o si el
 *   Auth Hook todavía no corrió sobre esta sesión (ver nota más abajo).
 * @property {string | null} empresa_personal_id - Empresa comercial propia
 *   del usuario cuando aplica. No reemplaza a empresa_id para RLS.
 * @property {'super_admin' | 'admin_empresa' | 'editor_empresa' | null} rol
 */

/** @type {ClaimsPlataforma} */
const CLAIMS_VACIOS = { empresa_id: null, empresa_personal_id: null, rol: null };

/**
 * Lee `empresa_id` y `rol` desde el JWT de la sesión actual.
 *
 * Estos son *custom claims* que `custom_access_token_hook` agrega en la
 * raíz del token (no dentro de `app_metadata`/`user_metadata`), así que
 * `session.user` tal como lo entrega supabase-js NO los incluye — hay que
 * decodificar el JWT. Nada de lo que se lee aquí es secreto: es exactamente
 * lo que ya viaja, firmado, dentro del access token que el navegador ya
 * tiene en memoria.
 *
 * Devuelve `{ empresa_id: null, rol: null }` si no hay sesión, si el token
 * no trae los claims (por ejemplo, una sesión emitida antes de asignarle
 * empresa al usuario — se corrige sola en el siguiente refresh de token),
 * o si el payload no se puede parsear.
 *
 * @param {import('@supabase/supabase-js').Session | null | undefined} session
 * @returns {ClaimsPlataforma}
 */
export function getClaims(session) {
  const payload = session?.access_token?.split('.')[1];
  if (!payload) return CLAIMS_VACIOS;

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((caracter) => '%' + caracter.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    const claims = JSON.parse(json);
    return {
      empresa_id: claims.empresa_id ?? null,
      empresa_personal_id: claims.empresa_personal_id ?? null,
      rol: claims.rol ?? null,
    };
  } catch {
    return CLAIMS_VACIOS;
  }
}
